import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import Box from "@mui/material/Box";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { BadgeProps } from "@/components/Badge";
import { Badge } from "@/components/Badge";
import { Tooltip } from "@/components/Tooltip";
import type { InterludeTypes } from "@/interlude";

type SafeApiKeyStatus = InterludeTypes["SafeApiKeyStatus"];

interface ApiKeyStatusBadgeProps {
  status: SafeApiKeyStatus;
}

export const ApiKeyStatusBadge: React.FC<ApiKeyStatusBadgeProps> =
  function ApiKeyStatusBadge({ status }) {
    const { t } = useTranslation();

    const { label, variant } = useMemo<{
      label: string;
      // `NonNullable`, because `variant` is optional on `BadgeProps` and
      // `exactOptionalPropertyTypes` rejects handing an optional prop a value
      // that could be undefined. Every branch below picks one.
      variant: NonNullable<BadgeProps["variant"]>;
    }>(() => {
      /**
       * A status this build has no name for, from a backend that added one
       * ahead of us. `SafeApiKeyStatusSchema` is what keeps that row in the
       * list at all -- a bare enum would fail the array's parse and empty the
       * whole page -- so the cell has to render something.
       *
       * The word is hardcoded and deliberately not translated, following
       * `TypeBadge`: it stands in for a raw backend value that was never
       * translatable either, and a catalog entry would outlive the situation.
       */
      if (!status.expected) return { label: "Unknown", variant: "plain" };

      const knownStatus = status.value;
      switch (knownStatus) {
        case "active":
          return {
            label: t("apiKey.status.active", "Active"),
            variant: "positive",
          };
        case "disabled":
          return {
            label: t("apiKey.status.disabled", "Disabled"),
            variant: "disable",
          };
        case "expired":
          return {
            label: t("apiKey.status.expired", "Expired"),
            variant: "attentive",
          };
        default:
          knownStatus satisfies never;
          throw new Error(
            inspectMessage`Invalid api key status ${knownStatus}`,
          );
      }
    }, [t, status]);

    const badge = <Badge variant={variant}>{label}</Badge>;

    if (status.expected) return badge;

    /**
     * The raw value, for whoever has to work out what the row is actually in.
     * `describeChild` keeps `Unknown` as the badge's accessible name and makes
     * the raw value a description, so a screen reader announces both rather
     * than replacing the one word a listener can act on.
     */
    return (
      <Tooltip title={status.raw} arrow describeChild>
        <Box component="span">{badge}</Box>
      </Tooltip>
    );
  };
